import { DEFAULT_ROASTER_NOTE_MAX_LENGTH } from "@client-shared/lib/constants";
import { htmlToPlainText } from "@client-shared/utilities";
import { sanitizeAeroPressGrind } from "@client-shared/utilities/productSyncUtilities";
import { estimateAgtronFromIntensity } from "@shared/lib/intensity/intensity-estimator";
import type { ProductTargetMetadata, SalesforceProduct } from "@shared/services";
import type * as T from "@shared/types";
import { isIntensityPosition, toSalesforceIntensityZone } from "@shared/types/intensity";
import { v4 as generateUUID } from "uuid";
import { blobToBase64 } from "./imageProcessing";

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Form Processing

const setGrindTypes = (variants: T.FormVariant[]): string => {
  const allGrinds = variants.flatMap((variant) => {
    const grindInfo = variant.detectedGrindInfo;
    if (!variant.hasDetectedGrindOverride && grindInfo?.some((detectedGrind) => detectedGrind.mapDetectedGrindTo)) {
      return grindInfo.map((detectedGrind) => detectedGrind.mapDetectedGrindTo ?? detectedGrind.grind);
    }
    return variant.grind ?? [];
  });

  // Return unique grinds
  return [...new Set(allGrinds)].join(";");
};

const setId = (variant: T.FormVariant): string | null => {
  return variant.status === "Rejected" && variant.serviceId ? variant.serviceId : null;
};

const setBagSize = (variant: T.FormVariant): string => {
  if (variant.mapDetectedBagSizeTo) {
    return variant.mapDetectedBagSizeTo;
  }
  return variant.bagSize;
};

export const setExternalProductId = (productId: string, bagSize: string): string => {
  return productId + "-" + bagSize.replaceAll(" ", "") + "-" + generateUUID();
};

export const setConditionalOrNull = <T>(value: T | undefined): T | null => {
  return value ?? null;
};

/** Resolves the `Wholesale_price__c` value to send for one variant of a
 *  product. Defaults to the raw entered value unchanged; callers that need
 *  region-specific tax rules (e.g. the DE-domestic KS-excluded wholesale
 *  rule) inject an override — kept as a callback rather than an import so
 *  this shared utility never depends on `bcc`-specific tax logic. */
export type ResolveWholesalePrice = (product: T.FormProduct, variant: T.FormVariant) => number | string | undefined;

const defaultResolveWholesalePrice: ResolveWholesalePrice = (_product, variant) => variant?.wholesalePrice;

export const mapProductToSalesforceProduct =
  (
    userId: string,
    currencyIso: T.CurrencyIsoCode,
    vendorRegion: T.VendorRegion,
    installationId: string,
    regionLocalesObject: LocalizedFieldLocaleObject[],
    resolveWholesalePrice: ResolveWholesalePrice = defaultResolveWholesalePrice
  ) =>
  async (selectedProduct: T.FormProduct) => {
    const salesforceProduct = await createSalesforceProduct(selectedProduct, userId, currencyIso, vendorRegion, regionLocalesObject);
    const { id, variants } = selectedProduct;

    // get the selected variants from the form
    const selectedVariants = variants.filter((variant) => variant?.isProductSelectedForImport);
    const bagSizes = [...new Set(selectedVariants.map((variant) => variant.bagSize))];
    const variantsPerBagSize = bagSizes.map((bagSize) => {
      return selectedVariants.filter((variant) => variant.bagSize === bagSize);
    });

    // create a product per each selected bag size
    const productsPerVariant = variantsPerBagSize.map((variants) => {
      const variant = variants[0] as T.FormVariant;
      return {
        ...salesforceProduct,
        Id: setId(variant),
        Bag_size__c: setBagSize(variant),
        External_Product_ID__c: setExternalProductId(id, variant.bagSize),
        Grind__c: setGrindTypes(variants),
        MSRP__c: variant.price,
        Name_of_coffee__c: salesforceProduct.Name_of_coffee__c,
        Roaster_effective_date__c: variant?.startDate,
        Out_of_Stock_Date__c: setConditionalOrNull(variant?.endDate),
        Term__c: variant.term,
        Wholesale_price__c: resolveWholesalePrice(selectedProduct, variant),
        Weekly_Inventory__c: variant?.weeklyInventory,
        Target_Metadata__c: JSON.stringify(setTargetMetadata(id, installationId, variants, selectedProduct.ship_to)),
        Total_Inventory_for_Season__c: setConditionalOrNull(variant?.totalMaxShortTermInventory),
        Web_price__c: variant.webPrice,
      };
    });
    return productsPerVariant;
  };

export type LocalizedFieldLocaleObject = Pick<T.LocaleObject, "locale" | "isDefault" | "isEnglish">;

export const getLocalisedFieldByDefaultOrEnglish =
  (regionLocalesObject: LocalizedFieldLocaleObject[], localizedFields?: T.LocalizedFields) =>
  (prefer: "default" | "English", key: T.LocalizedFieldKey) => {
    if (!localizedFields) {
      return null;
    }

    const locale = regionLocalesObject.find((obj) => {
      if (prefer === "default") {
        return obj.isDefault;
      }
      return obj.isEnglish;
    })?.locale;

    if (!locale || !localizedFields[locale]) {
      return null;
    }

    const fieldValue = localizedFields[locale]?.[key] ?? null;
    if (key === "roasterNotes" && fieldValue && fieldValue.length > DEFAULT_ROASTER_NOTE_MAX_LENGTH) {
      return fieldValue.substring(0, DEFAULT_ROASTER_NOTE_MAX_LENGTH - 1);
    }
    return fieldValue;
  };

export const setLocalizedFields = (selectedProduct: T.FormProduct, regionLocalesObject: LocalizedFieldLocaleObject[], isLocal: boolean) => {
  if (!isLocal) {
    return {
      Roaster_Notes_Locale__c: null,
      Product_description_Locale__c: null,
      Product_description_Copyrighted__c: null,
      Roaster_Comments_Copyrighted__c: null,
    };
  }

  const curried = getLocalisedFieldByDefaultOrEnglish(regionLocalesObject, selectedProduct.localizedFields);
  return {
    Roaster_Notes_Locale__c: curried("default", "roasterNotes"),
    Product_description_Locale__c: curried("default", "description"),
    Product_description_Copyrighted__c: curried("English", "description"),
    Roaster_Comments_Copyrighted__c: curried("English", "roasterNotes"),
  };
};

/**
 * The estimated Agtron pair for a position: the estimate with
 * `Is_Agtron_Score_Provided__c` false, or null/null to clear it once the
 * coffee has no position. Never a measured reading.
 */
export const buildAgtronEstimateFields = (
  intensityPosition: unknown
): Pick<SalesforceProduct, "Agtron_Score__c" | "Is_Agtron_Score_Provided__c"> =>
  isIntensityPosition(intensityPosition)
    ? { Agtron_Score__c: estimateAgtronFromIntensity(intensityPosition), Is_Agtron_Score_Provided__c: false }
    : { Agtron_Score__c: null, Is_Agtron_Score_Provided__c: null };

/**
 * Provisional intensity score. The beans manager reviews it after tasting and
 * promotes the result to the PIM WEB_INTENSITY_* fields, so what lands here is
 * the roaster's own estimate, not an approved value. A coffee with no position
 * sends no intensity fields at all. `closestMatchCoffee` is the roaster's
 * "most similar to" answer the position was derived from.
 *
 * Once the coffee is established — it is on PIM (scored or not) or has an
 * earlier approved request — the request must not carry a new score. Omitting
 * the keys keeps the established score and Agtron reading untouched, and only
 * the established "most similar to" pick is sent.
 */
const buildIntensityFields = (
  intensityZone: unknown,
  intensityPosition: unknown,
  closestMatchCoffee: string | null | undefined,
  isEstablished: boolean
): Partial<SalesforceProduct> => {
  if (isEstablished) {
    // `undefined` means the form does not manage the pick; otherwise a cleared
    // pick is sent as null so an in-place update cannot keep a stale one.
    return closestMatchCoffee === undefined ? {} : { Closest_Match_Coffee__c: closestMatchCoffee || null };
  }
  return isIntensityPosition(intensityPosition)
    ? {
        Intensity_Zone__c: toSalesforceIntensityZone(intensityZone),
        Intensity_Position__c: intensityPosition,
        ...buildAgtronEstimateFields(intensityPosition),
        ...(closestMatchCoffee === undefined ? {} : { Closest_Match_Coffee__c: closestMatchCoffee || null }),
      }
    : {};
};

// Mirrors the bcc `isProductLevelLocked` rule: a variant already sent to
// Salesforce (Submitted/Pending, or imported and not rejected) has carried the
// coffee's intensity, so a later request for the same coffee must not resend it.
const hasSubmittedVariant = (variants: T.FormVariant[] | undefined): boolean =>
  Boolean(
    variants?.some(
      (variant) =>
        variant.isImportedLocalVariant ||
        (variant.isImportedRemote && variant.status !== "Rejected" && variant.status !== "Draft") ||
        variant.status === "Submitted" ||
        variant.status === "Pending"
    )
  );

const setInitialSalesforceProduct = (
  isLocal: boolean,
  selectedProduct: T.FormProduct,
  userId: string,
  currencyIso: T.CurrencyIsoCode,
  vendorRegion: T.VendorRegion,
  isImportedRemote: boolean
): Partial<SalesforceProduct> => {
  const {
    badges,
    brewMethod,
    certifications,
    character,
    country,
    description,
    elevationEnd,
    elevationStart,
    mainFlavorCategory,
    process,
    productType,
    region,
    roastLevel,
    secondaryFlavorCategory,
    tastingNotes,
    title,
    localTitle,
    varietal,
    roasterNotes,
    ship_to,
    intensityZone,
    intensityPosition,
    closestMatchCoffee,
    hasApprovedIntensity,
    localVariants,
  } = selectedProduct;
  // Intensity is set once per coffee, so a coffee already on PIM (imported
  // variants) or already submitted never sends a new score, even when PIM has
  // none yet.
  const isIntensityLocked = Boolean(hasApprovedIntensity || localVariants?.length || hasSubmittedVariant(selectedProduct.variants));

  return {
    Badges__c: setConditionalOrNull(badges?.join(";")),
    Character__c: setConditionalOrNull(character),
    Brew_Method__c: brewMethod?.join(";"),
    Business_unit__c: vendorRegion,
    Certifications_Accolades_or_Awards__c: setConditionalOrNull(certifications?.join(";")),
    Country__c: country,
    // Cross-border ship-to destinations — a ";"-separated string (e.g. "NL;DE")
    // carried from the ProductForm MultiSelect. Empty/absent → null.
    Ship_To__c: ship_to || null,
    CurrencyIsoCode: currencyIso,
    Elevation_from__c: setConditionalOrNull(elevationStart),
    Elevation_to__c: setConditionalOrNull(elevationEnd),
    Image_Doc__c: null,
    Is_this_coffee_decaf__c: selectedProduct.isDecaf ?? false,
    Main_flavor_category__c: mainFlavorCategory,
    Name_of_coffee__c: isImportedRemote || isLocal ? localTitle : title,
    Process__c: setConditionalOrNull(process),
    Product_description__c: htmlToPlainText(description) ?? " ",
    Region__c: region,
    Requestor__c: userId,
    Roaster_notes__c: roasterNotes ?? "",
    Roast_level__c: roastLevel,
    ...buildIntensityFields(intensityZone, intensityPosition, closestMatchCoffee, isIntensityLocked),
    Secondary_flavor_category__c: setConditionalOrNull(secondaryFlavorCategory),
    Tasting_notes__c: tastingNotes?.join(";"),
    Type_of_coffee__c: productType,
    Varietal__c: setConditionalOrNull(varietal),
    Is_New_Bag_Sku__c: isLocal,
    Name_of_coffee_copyrighted__c: isLocal ? localTitle : null,
  };
};

const createSalesforceProduct = async (
  selectedProduct: T.FormProduct,
  userId: string,
  currencyIso: T.CurrencyIsoCode,
  vendorRegion: T.VendorRegion,
  regionLocalesObject: LocalizedFieldLocaleObject[]
): Promise<Partial<SalesforceProduct>> => {
  const { imageUploads, localVariants } = selectedProduct;
  const isLocal = localVariants?.length ? localVariants.length > 0 : false;
  const isImportedRemote = selectedProduct.variants.some((variant) => variant.isImportedRemote && variant.status !== "Rejected");

  // set the common properties for the selected variants
  // set optional null only to the optional keys
  let salesforceProduct = setInitialSalesforceProduct(isLocal, selectedProduct, userId, currencyIso, vendorRegion, isImportedRemote);
  const localizedFields = setLocalizedFields(selectedProduct, regionLocalesObject, isLocal);
  const image = await getImage(isLocal, imageUploads);
  salesforceProduct = {
    ...salesforceProduct,
    ...localizedFields,
    Image_Doc__c: image,
  };
  return salesforceProduct;
};

const getImage = async (useUrl: boolean, imageUploads: T.UploadImage[] | null | undefined) => {
  let url: string | null | undefined = null;
  if (useUrl) {
    url = imageUploads?.[0]?.url ?? null;
  } else {
    url = await setImageUploads(imageUploads);
  }
  return url;
};

export const UNAUTHORIZED_SYMBOLS = ["®"];

export const setTargetMetadata = (
  productId: string,
  installationId: string,
  variants: T.FormVariant[],
  shipTo?: string | null
): ProductTargetMetadata => {
  // Cross-border prices are per bag size; every grind variant in the group
  // shares them, so take the first variant's computed skuPrices. Omitted
  // entirely for non-cross-border products (no skuPrices on the variant).
  const computedSkuPrices = variants.find((variant) => variant.skuPrices?.length)?.skuPrices;
  // Restrict to the SKU's selected ship-to regions. The cross-border price block
  // only recomputes while more than one region is selected, so narrowing the
  // selection (e.g. DE+NL → DE) leaves a stale price entry for the dropped region
  // on the variant. Filtering here guarantees the metadata only carries prices for
  // regions the SKU actually ships to.
  const selectedRegions = (shipTo ?? "")
    .split(";")
    .map((region) => region.trim())
    .filter(Boolean);
  const filteredSkuPrices =
    computedSkuPrices && selectedRegions.length > 0
      ? computedSkuPrices.filter((price) => selectedRegions.includes(price.region))
      : computedSkuPrices;
  const skuPrices = filteredSkuPrices?.length ? filteredSkuPrices : undefined;
  return {
    version: "v1",
    targetInstallationId: installationId,
    targetProductId: productId,
    skuPrices,
    variants: variants.flatMap((variant) => {
      if (variant.detectedGrindInfo?.some((detectedGrind) => detectedGrind.mapDetectedGrindTo)) {
        return variant.detectedGrindInfo.map((detectedGrind) => {
          return {
            targetVariantId: variant.id,
            grindType: sanitizeAeroPressGrind(detectedGrind.mapDetectedGrindTo ?? detectedGrind.grind),
          };
        });
      }
      if (!variant.grind) {
        return {
          targetVariantId: variant.id,
        };
      }
      return variant.grind.map((grind) => ({
        targetVariantId: variant.id,
        grindType: sanitizeAeroPressGrind(grind),
      }));
    }),
  };
};

const setImageUploads = async (imageUploads: T.UploadImage[] | undefined | null) => {
  if (!imageUploads || imageUploads.length === 0) {
    return null;
  }
  const imageUpload = imageUploads[0];
  if (imageUpload?.type === "url") {
    return imageUpload.url;
  }
  if (imageUpload?.type === "file") {
    const base64 = await blobToBase64(imageUpload.file);
    return base64.split("base64,")[1] as string;
  }
};

export const setAllRemoteProductsToSalesforceProducts = async (
  selectedProducts: T.FormProduct[],
  userId: string,
  currencyIsoCode: T.CurrencyIsoCode,
  vendorRegion: T.VendorRegion,
  installationId: string,
  regionLocalesObject: LocalizedFieldLocaleObject[],
  resolveWholesalePrice?: ResolveWholesalePrice
) => {
  const curriedMapProductToSalesforceProduct = mapProductToSalesforceProduct(
    userId,
    currencyIsoCode,
    vendorRegion,
    installationId,
    regionLocalesObject,
    resolveWholesalePrice
  );
  // Each product maps independently (its own image/localisation work), so
  // this can run concurrently rather than serialising one product at a time.
  const mappedProductsPerProduct = await Promise.all(selectedProducts.map((product) => curriedMapProductToSalesforceProduct(product)));
  return mappedProductsPerProduct.flat() as SalesforceProduct[];
};
